import { RollforwardActivities } from "PFApp/activities/show/activity_page/parts/tabs/overview/rollforward_info_section/roll_forward_activities";
import { RollforwardProfiles } from "PFApp/activities/show/activity_page/parts/tabs/overview/rollforward_info_section/roll_forward_profiles";
import { useActivityShowContext } from "PFApp/activities/show/context/activity_show_context";
import { useCurrentActivity } from "PFApp/activities/show/hooks/use_current_activity";
import { DetailsPanelType, useDetailsPanelsActions } from "PFApp/booking/components/details_panel";
import { useProfilePreview } from "PFApp/use_profile_preview";
import { Stack } from "PFComponents/containers";
import { Divider } from "PFComponents/divider";
import { Typography } from "PFComponents/typography";
import { Activity, RollforwardProfile, RollforwardReference } from "PFCore/types";
import { useTranslation } from "react-i18next";

import css from "./roll_forward_info_content.module.scss";

type RollForwardInfoContentProps = {
  activity?: Activity;
  isRole?: boolean;
  mode?: "button" | "link";
  isVertical?: boolean;
  allowPanelStack?: boolean;
};

export const RollForwardInfoContent = ({
  activity: activityProp,
  isRole: isRoleProp,
  mode,
  isVertical,
  allowPanelStack = true
}: RollForwardInfoContentProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "show.details.rollforward" });

  const currentActivity = useCurrentActivity();
  const { isRole: isRoleFromContext } = useActivityShowContext();
  const { openDetailsPanel, closeAllDetailsPanels } = useDetailsPanelsActions();
  const { showPreview, hidePreview } = useProfilePreview();

  const activity = activityProp ?? currentActivity;
  const isRole = isRoleProp ?? isRoleFromContext;
  const rollforwardInfo = activity?.rollforward_info;

  if (!rollforwardInfo) {
    return null;
  }

  const activityType = isRole ? DetailsPanelType.Role : DetailsPanelType.Engagement;
  const { source, targets } = rollforwardInfo;
  const bookedProfiles = source?.booked_profiles;
  const hasContent = source || !!targets?.length || (isRole && bookedProfiles?.length);
  const hasDivider = !isVertical && !!source && !!targets?.length;

  if (!hasContent) {
    return null;
  }

  const handleActivityClick = (reference: RollforwardReference) => {
    if (!allowPanelStack) {
      hidePreview();
    }
    openDetailsPanel({ id: reference.id, type: activityType }, allowPanelStack);
  };

  const handleProfileClick = (profile: RollforwardProfile) => {
    if (!allowPanelStack) {
      closeAllDetailsPanels();
    }
    showPreview({ id: profile.id as number });
  };

  const sourceContent = source && (
    <>
      <Stack gap="spacingXs">
        <Typography variant="labelRegular">{isRole ? t("sourceRole") : t("sourceEngagement")}</Typography>
        <RollforwardActivities activities={[source]} mode={mode} onActivityClick={handleActivityClick} />
      </Stack>
      {isRole && bookedProfiles && bookedProfiles.length > 0 && (
        <Stack gap="spacingXs">
          <Typography variant="labelRegular">{t("bookedProfiles")}</Typography>
          <RollforwardProfiles
            profiles={bookedProfiles}
            mode={mode}
            onProfileClick={handleProfileClick}
            isVertical={isVertical}
          />
        </Stack>
      )}
    </>
  );

  return (
    <Stack gap="spacingLg">
      <Typography variant="h5">{t("title")}</Typography>
      {source && (
        <Stack>
          <Typography variant="bodyRegular">
            {isRole ? t("sourceRoleSubtitle") : t("sourceEngagementSubtitle")}
          </Typography>
          {isVertical ? (
            <Stack>{sourceContent}</Stack>
          ) : (
            <div className={css.gridContainer}>{sourceContent}</div>
          )}
        </Stack>
      )}

      {hasDivider && <Divider />}
      {targets && targets.length > 0 && (
        <Stack>
          <Typography variant="bodyRegular">
            {isRole ? t("targetRoleSubtitle") : t("targetEngagementSubtitle")}
          </Typography>
          <Typography variant="labelRegular">{isRole ? t("targetRoles") : t("targetEngagements")}</Typography>
          <Stack gap="spacingXs">
            <RollforwardActivities activities={targets} mode={mode} onActivityClick={handleActivityClick} />
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
